import clsx from 'clsx';
import type { HTMLAttributeAnchorTarget, ReactNode } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSInternalLink } from '@repo/sds/components/_internal/SDSInternalLink/SDSInternalLink';
import { getExternalLinkProps } from '@repo/sds/components/SDSLink/utils';

import '@repo/sds/components/SDSLink/SDSLink.scss';

export interface SDSLinkProps {
  children: ReactNode;
  href: string;
  className?: string;
  dataTestId?: string;
  target?: HTMLAttributeAnchorTarget;
  // Prefetch strategy for internal links (ignored for external links)
  prefetch?: 'intent' | 'render' | 'viewport' | 'none';
  // Accessibility props
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  ariaExpanded?: boolean;
  ariaHaspopup?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  role?: string;
  tabIndex?: number;
  // Event handlers
  onFocus?: (event: React.FocusEvent<HTMLAnchorElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLAnchorElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

// Helper to build common props
const buildCommonProps = (
  props: Omit<SDSLinkProps, 'children' | 'href' | 'target' | 'ariaLabel' | 'prefetch'>,
  enhancedAriaLabel: string | undefined,
) => ({
  className: props.className,
  'data-testid': props.dataTestId || DataTestIds.SDS.SDS_LINK,
  ...(enhancedAriaLabel != null && { 'aria-label': enhancedAriaLabel }),
  ...(props.ariaDescribedBy != null && { 'aria-describedby': props.ariaDescribedBy }),
  ...(props.ariaCurrent != null && { 'aria-current': props.ariaCurrent }),
  ...(props.ariaExpanded != null && { 'aria-expanded': props.ariaExpanded }),
  ...(props.ariaHaspopup != null && { 'aria-haspopup': props.ariaHaspopup }),
  ...(props.role != null && { role: props.role }),
  ...(props.tabIndex != null && { tabIndex: props.tabIndex }),
  onFocus: props.onFocus,
  onBlur: props.onBlur,
  onClick: props.onClick,
  onMouseEnter: props.onMouseEnter,
});

export function SDSLink(props: SDSLinkProps) {
  const { children, className, href, target, ariaLabel, prefetch } = props;
  const linkClassName = clsx('SDSLink', className);
  const { target: linkTarget, rel, isExternal } = getExternalLinkProps(href, target);

  const commonProps = buildCommonProps({ ...props, className: linkClassName }, ariaLabel);

  if (isExternal) {
    return (
      <a href={href} target={linkTarget} rel={rel} {...commonProps}>
        {children}
        {linkTarget === '_blank' && !ariaLabel ? (
          <span className="SDSLink__external-assistive-text"> (opens in new tab)</span>
        ) : null}
      </a>
    );
  }

  return (
    <SDSInternalLink href={href} target={target} prefetch={prefetch} {...commonProps}>
      {children}
    </SDSInternalLink>
  );
}
